/* eslint-disable jsx-a11y/anchor-is-valid */
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  menuBookEditorActions,
  selectMenuBookEditorCouponClassificationSource,
  selectMenuBookEditorExpandAll,
  selectMenuBookEditorFormSearchValue,
} from 'features/MenuBook/menuBookEditorSlice';
import { Menu } from 'features/MenuBook/models';
import MenuDetailModal from 'features/MenuDetail/pages/MenuDetail';
import { Suspense, memo, useEffect, useRef, useState } from 'react';
import { Draggable, DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { Form, ModalProps } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { MINUS_TEXT, PLUS_TEXT, PRIMARY_BUTTON } from 'utils';
import { wasMultiSelectKeyUsed, wasToggleInSelectionGroupKeyUsed } from '../../utils';
import { COLUMN_WIDTH, getItemStyle } from '../MenuBookEditorModal';

export interface MenuCollapseProps {
  genreId: number;
  genreName: string;
  subgenreId: number;
  subgenreName: string;
  menuId: number;
  children?: React.ReactNode;
  index: number;
  onSelectedRowChanged: () => void;
}

function MenuRow({
  genreId,
  genreName,
  subgenreId,
  subgenreName,
  menuId,
  children,
  index,
  onSelectedRowChanged,
}: MenuCollapseProps) {
  const dispatch = useAppDispatch();

  const menu = useAppSelector((state) => {
    return state.menuBookEditor.data
      .flatMap((genre) => (genre.id === genreId ? genre.subgenres : []))
      .flatMap((subgenre) => (subgenre.id === subgenreId ? subgenre.menus : []))
      .find((menu) => menu.id === menuId);
  }) as Menu;

  const isExpand = useAppSelector(selectMenuBookEditorExpandAll);
  const formSearchValue = useAppSelector(selectMenuBookEditorFormSearchValue);

  const couponClassificationSource = useAppSelector(selectMenuBookEditorCouponClassificationSource);
  const [open, setOpen] = useState(false);
  const [showMenuModal, setShowMenuModal] = useState(false);

  // ref
  const menuDetailModalRef = useRef<ModalProps>(MenuDetailModal);
  const isLoadedChildRef = useRef(false);

  const pathId = `${genreId}-${subgenreId}-${menu.id}`;
  const droppableId = `subgenre-${genreId}-${subgenreId}`;
  const isSelected = false;
  const isGhosting = false;
  const mbEditorContentElement = document.getElementById('mb-editor-table');

  useEffect(() => {
    const btnExpandElem = document.querySelector(`#menu-${pathId}-row .dan-btn-expand`) as HTMLButtonElement;
    function handleBtnExpandElemClick(event: MouseEvent) {
      event.stopPropagation();
      setOpen(!open);
      isLoadedChildRef.current = true;
      btnExpandElem.innerHTML = !open ? MINUS_TEXT : PLUS_TEXT;
      btnExpandElem?.setAttribute('aria-expanded', !open ? 'true' : 'false');
    }

    if (btnExpandElem) {
      btnExpandElem.addEventListener('click', handleBtnExpandElemClick);
    }

    return () => {
      if (btnExpandElem) {
        btnExpandElem.removeEventListener('click', handleBtnExpandElemClick);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (isExpand !== undefined) {
      setOpen(isExpand && isLoadedChildRef.current);
    }
  }, [isExpand]);

  useEffect(() => {
    if (showMenuModal) {
      handleOpenMenuDetailModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMenuModal]);

  function onKeyDown(
    event: React.KeyboardEvent<HTMLDivElement>,
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot
  ) {
    if (event.defaultPrevented) {
      return;
    }

    if (snapshot.isDragging) {
      return;
    }

    if (event.key !== 'Enter') {
      return;
    }

    // we are using the event for selection
    event.preventDefault();

    performAction(event);
  }

  // Using onClick as it will be correctly
  // preventing if there was a drag
  function onClick(event: React.MouseEvent<HTMLDivElement>) {
    const targetElement = event.target as HTMLElement;
    const isInsideParent = mbEditorContentElement && mbEditorContentElement.contains(targetElement);
    if (!isInsideParent) {
      return;
    }

    if (event.defaultPrevented) {
      return;
    }

    if (event.button !== PRIMARY_BUTTON) {
      return;
    }

    // marking the event as used
    // const targetElement = event.target as HTMLElement;
    if (
      !targetElement.classList.contains('form-check-input') &&
      !targetElement.classList.contains('dan-btn-expand') &&
      !Array.from(targetElement.classList).some((className) => className.includes('btn'))
    ) {
      event.preventDefault();
    } else {
      return;
    }

    performAction(event);
  }

  function onTouchEnd(event: React.TouchEvent<HTMLDivElement>) {
    const targetElement = event.target as HTMLElement;
    const isInsideParent = mbEditorContentElement && mbEditorContentElement.contains(targetElement);
    if (!isInsideParent) {
      return;
    }

    if (event.defaultPrevented) {
      return;
    }

    // marking the event as used
    // we would also need to add some extra logic to prevent the click
    // if this element was an anchor
    if (
      !targetElement.classList.contains('form-check-input') &&
      !targetElement.classList.contains('dan-btn-expand') &&
      !Array.from(targetElement.classList).some((className) => className.includes('btn'))
    ) {
      event.preventDefault();
    }

    dispatch(menuBookEditorActions.toggleSelectionInGroup({ selectedDroppableId: droppableId, itemId: menu.id }));
  }

  function performAction(event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) {
    if (wasToggleInSelectionGroupKeyUsed(event)) {
      dispatch(menuBookEditorActions.toggleSelectionInGroup({ selectedDroppableId: droppableId, itemId: menu.id }));
      return;
    }

    if (wasMultiSelectKeyUsed(event)) {
      dispatch(
        menuBookEditorActions.multiSelectTo({
          arg: {
            // data,
            // selectedItemIds,
            type: 'menu',
            droppableId: droppableId,
          },
          newItemId: menu.id,
        })
      );
      return;
    }

    dispatch(menuBookEditorActions.toggleSelection({ selectedDroppableId: droppableId, itemId: menu.id }));
  }

  function handleOpenMenuModal() {
    setShowMenuModal(true);
  }

  function handleOpenMenuDetailModal() {
    handleOpenPopupSelectedMenuId(menu.id);
  }

  function handleMenuDetailModalClose() {
    menuDetailModalRef.current.onReset();
    setShowMenuModal(false);
  }

  function handleOpenPopupSelectedMenuId(menuId: number) {
    if (formSearchValue && formSearchValue.dataDate) {
      const strDataDate =
        formSearchValue.dataDate.getFullYear() +
        '' +
        (formSearchValue.dataDate.getMonth() + 1) +
        '' +
        formSearchValue.dataDate.getDate();
      menuDetailModalRef.current.openModalExternal(menuId, strDataDate);
    }
  }

  return (
    <Draggable draggableId={`menu-${pathId}`} index={index}>
      {(provided, snapshot) => {
        return (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={getItemStyle(snapshot.isDragging, provided.draggableProps.style, isSelected, isGhosting)}
            onClick={onClick}
            onTouchEnd={onTouchEnd}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => onKeyDown(event, provided, snapshot)}
          >
            <>
              <div id={`menu-${pathId}-row`} className="c-menu-row dan-tr">
                <div className="dan-td d-flex justify-content-center" style={{ width: `${COLUMN_WIDTH[0]}%` }}>
                  {!menu.deleted && (
                    <Form.Check
                      className="chk-mbeditor-item custom-checkbox"
                      data-type="menu"
                      data-deletetype="menus"
                      data-genreid={genreId}
                      data-genre_name={genreName}
                      data-subgenreid={subgenreId}
                      data-subgenre_name={subgenreName}
                      data-subgenre_menu_id={menu.subgenre_menu_id || -1}
                      defaultValue={menu.id}
                      data-menu_name={menu.name}
                      tabIndex={-1}
                      onChange={onSelectedRowChanged}
                    ></Form.Check>
                  )}
                </div>
                <div className="dan-td" style={{ width: `${COLUMN_WIDTH[1] + COLUMN_WIDTH[2]}%` }}></div>
                <div
                  className="dan-td"
                  style={{ width: `${COLUMN_WIDTH.slice(3, 10).reduce((sum, width) => sum + width, 0)}%` }}
                >
                  <button
                    className="dan-btn-expand"
                    tabIndex={-1}
                    aria-controls={`menu-${pathId}-content`}
                    aria-expanded={false}
                  >
                    {open && isLoadedChildRef.current ? MINUS_TEXT : PLUS_TEXT}
                  </button>
                  <a
                    className="px-0 btn btn-link"
                    style={{ minWidth: 'inherit', maxWidth: '180px' }}
                    rel="noreferrer"
                    tabIndex={-1}
                    onClick={handleOpenMenuModal}
                  >
                    {menu.name}{' '}
                    {couponClassificationSource &&
                      `(${couponClassificationSource[Number(menu.coupon_classification)]})`}
                  </a>
                </div>
              </div>
              <div style={{ display: open && isLoadedChildRef.current ? 'block' : 'none' }}>
                {isLoadedChildRef.current ? (
                  <div id={`menu-${pathId}-content`}>{children}</div>
                ) : (
                  open && (
                    <Suspense
                      fallback={
                        <div className="dan-tr">
                          <div className="dan-td" style={{ width: '100%' }}>
                            Loading...
                          </div>
                        </div>
                      }
                    >
                      <div id={`menu-${pathId}-content`}>{children}</div>
                    </Suspense>
                  )
                )}
              </div>

              {showMenuModal &&
                createPortal(
                  <MenuDetailModal
                    ref={menuDetailModalRef}
                    onMenuModalClose={handleMenuDetailModalClose}
                    // menuCallBack={handleMenuDetailModalSaveSuccess}
                  />,
                  document.body
                )}
            </>
          </div>
        );
      }}
    </Draggable>
  );
}

export default memo(MenuRow);
